/**
 * The shape of a composed email — 16 Sep 2026.
 *
 * Until now a message was one string of HTML out of a contentEditable, which is
 * right for a note and wrong for anything with a header, a coloured panel and a
 * button in it. This is the document behind a message that looks composed:
 * an ordered list of sections, each with its own background, padding and
 * alignment, and each optionally addressed at one kind of reader.
 *
 * ## Why a document and not a rich-text field
 *
 * A background colour cannot be expressed in a contentEditable's markup in a way
 * that survives Outlook, and a two-column layout cannot be expressed in it at
 * all. Email HTML is tables with inline styles, which is not something to ask a
 * writer to type — so the writer edits sections and `renderEmail` produces the
 * tables. The rich text stays, inside a text section, where it is the right
 * tool: bold, italics, lists and links.
 *
 * ## Conditional sections, and the one thing they must not become
 *
 * A section may be addressed at readers on a trial, on a paid plan, on neither,
 * or on their way out. The reader's state comes from the product's own record of
 * their entitlement, resolved at send time, so a message can say one thing to a
 * subscriber and another to somebody who has never paid without splitting the
 * campaign in two.
 *
 * **The tiers are coarse on purpose.** Five buckets, derived from one field, and
 * no arithmetic beyond "is this still active". A condition language — nested
 * rules over arbitrary attributes — is the thing every email tool grows and then
 * cannot explain the output of, and the first time a send goes wrong nobody can
 * say who received what. Five named audiences can be listed on the screen beside
 * the section, and the preview can show each one.
 */

/**
 * What a reader is paying, as far as the product's own record knows.
 *
 * Derived from `users.activeEntitlements` in the consumer project — one field,
 * written by the app from RevenueCat, and already read by the audience resolver
 * on its way past. See `tierOf` in `functions-webhook/src/emailVariants.ts`.
 */
export type ReaderTier =
  /** No entitlement, and no sign of one having lapsed. */
  | 'free'
  /** An active entitlement inside its trial period. */
  | 'trial'
  /** An active, paid entitlement that is set to renew. */
  | 'paid'
  /** Active and paid for, but renewal is switched off — access ends at expiry. */
  | 'cancelling'
  /** Had an entitlement once; has none now. */
  | 'lapsed'

/**
 * What else a reader can be, on top of what they pay — 17 Sep 2026.
 *
 * Skida PRO membership is **orthogonal** to the tier: the person who runs a
 * guiding company is very often also a paying app subscriber, and one slot per
 * reader would have made somebody choose which of those two they are. So a
 * reader carries exactly one tier and, if they are in a PRO company, `pro` as
 * well — plus `pro-admin` if they run one. An admin always also counts as a
 * member, so a section addressed at `pro` reaches them too.
 *
 * Mirrored as `ReaderSegment` in `functions-webhook/src/emailVariants.ts`.
 */
export type ReaderSegment = ReaderTier | 'pro' | 'pro-admin' | ActivitySegment | PlaceSegment

/**
 * Where a reader is — 28 Oct 2026. `lang:no`, `country:NO`.
 *
 * Not chosen in `visibleTo` but in a section's own `languages` and
 * `countries`, because it is a different question: a section for Norwegian
 * readers who pay is Norwegian **and** paying, not either. A reader carries
 * one language always (English when nothing is known) and a country when
 * RevenueCat has seen them. Mirrored in `functions-webhook/src/emailVariants.ts`.
 */
export type PlaceSegment = `lang:${string}` | `country:${string}`

/**
 * What a reader has actually done — 18 Sep 2026.
 *
 * Behaviour rather than billing, so a section can be addressed at the people who
 * have drawn a route — or, more usefully, at the people who have not. They come
 * in pairs because `visibleTo` means "show only to": without the `no-` half,
 * "everyone who hasn't tried this yet" could not be expressed at all.
 *
 * Counted nightly into `skidaCrmUserStats`, so up to a day old. Mirrored as
 * `ActivitySegment` in `functions-webhook/src/emailVariants.ts`.
 */
export type ActivitySegment =
  | 'has-routes'
  | 'no-routes'
  | 'has-collections'
  | 'no-collections'
  | 'has-bookmarks'
  | 'no-bookmarks'
  | 'has-tracker'
  | 'no-tracker'

/**
 * The groups a section can be addressed at, as the editor lists them.
 *
 * Tiers first, then the PRO facts, then what the reader has actually done, with
 * a rule between each group on screen: they are three different questions, and a
 * list that ran them together would read as one long row of mutually exclusive
 * choices when in fact a reader carries one from each.
 *
 * The activity pairs are the answer to "don't show the draw-a-route teaser to
 * people who draw routes" — that section is addressed at `no-routes`.
 */
export const READER_TIERS: {
  key: ReaderSegment
  label: string
  note: string
  /** Where the divider goes in the picker. */
  group: 'tier' | 'pro' | 'activity'
}[] = [
  { key: 'free', label: 'Free', note: 'Never subscribed', group: 'tier' },
  { key: 'trial', label: 'On trial', note: 'Inside the trial period', group: 'tier' },
  { key: 'paid', label: 'Paying', note: 'Active and renewing', group: 'tier' },
  {
    key: 'cancelling',
    label: 'Cancelling',
    note: 'Active, renewal switched off',
    group: 'tier',
  },
  { key: 'lapsed', label: 'Lapsed', note: 'Subscribed once, not now', group: 'tier' },
  { key: 'pro', label: 'PRO member', note: 'In a Skida PRO company', group: 'pro' },
  {
    key: 'pro-admin',
    label: 'PRO admin',
    note: 'Runs a Skida PRO company — also counts as a member',
    group: 'pro',
  },
  { key: 'has-routes', label: 'Has drawn a route', note: 'At least one drawn route', group: 'activity' },
  {
    key: 'no-routes',
    label: 'Has drawn none',
    note: 'Never drawn a route — the audience for an invitation to try it',
    group: 'activity',
  },
  { key: 'has-collections', label: 'Has a collection', note: 'Has made at least one collection', group: 'activity' },
  { key: 'no-collections', label: 'No collections', note: 'Has never made one', group: 'activity' },
  {
    key: 'has-bookmarks',
    label: 'Has saved a route',
    note: 'Has something in Bookmarks — the lightest sign somebody means to come back',
    group: 'activity',
  },
  { key: 'no-bookmarks', label: 'Saved nothing', note: 'Bookmarks is empty', group: 'activity' },
  { key: 'has-tracker', label: 'Tracker connected', note: 'A live Garmin connection', group: 'activity' },
  {
    key: 'no-tracker',
    label: 'No tracker',
    note: 'Never connected one, or the connection has stopped working',
    group: 'activity',
  },
]

/**
 * The sections a message can be built from.
 *
 * The first five are the primitives; the rest are the components the designer
 * specified in the email handover of 17 Sep 2026 — a header, a heading, a dated
 * notice, a stat row, a card, numbered steps and a footer. They exist as their
 * own kinds rather than as text a writer styles by hand because the point of a
 * design system is that the defaults are already right: a stat row assembled
 * from a text block would be one writer's guess at 26px/700 on #F4F6F8, and the
 * next one's guess would be different.
 */
export type BlockKind =
  | 'text'
  | 'image'
  | 'button'
  | 'divider'
  | 'spacer'
  | 'header'
  | 'heading'
  | 'notice'
  | 'stats'
  | 'card'
  | 'steps'
  | 'footer'

export type BlockAlign = 'left' | 'center' | 'right'

/** Vertical breathing room, named rather than numbered — see `PADDING`. */
export type BlockPadding = 'none' | 'small' | 'medium' | 'large'

export const PADDING: Record<BlockPadding, number> = {
  none: 0,
  small: 12,
  medium: 24,
  large: 40,
}

/** A section's space above and below, in px. */
export function paddingOf(b: Pick<EmailBlock, 'padding' | 'paddingBottom'>): { top: number; bottom: number } {
  const top = PADDING[b.padding] ?? 24
  return { top, bottom: b.paddingBottom ? (PADDING[b.paddingBottom] ?? top) : top }
}

/**
 * The design system's own values, from the email handover of 17 Sep 2026.
 *
 * Copied here rather than read from `tokens/colors.css` because that file is a
 * stylesheet for the app and these have to end up as inline hex in an email —
 * an inbox resolves no custom property. Grep `EMAIL_THEME` when the palette
 * moves.
 */
export const EMAIL_THEME = {
  /** Outside the content column. */
  page: '#E4E9EF',
  surface: '#FFFFFF',
  /** The tinted panel a stat cell and a card sit on. */
  panel: '#F4F6F8',
  /** The pale blue behind a dated notice. */
  pale: '#F2F4FC',
  heading: '#111212',
  body: '#383838',
  muted: '#6E7A83',
  primary: '#3456DA',
  indigo: '#000B38',
  line: '#E1E6EA',
  /** The dotted underline under a merged value. */
  dotted: '#8EA1EC',
  font: "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif",
} as const

/** The wordmark, as the header block draws it. Absolute — an inbox has no root. */
export const WORDMARK = {
  light: 'https://internal.skida.app/email/skida-wordmark-indigo.png',
  dark: 'https://internal.skida.app/email/skida-wordmark-white.png',
} as const

/**
 * Our profiles, as the footer links them — 18 Oct 2026.
 *
 * Each brand's own mark in its own colour (Simple Icons, the brands' official
 * shapes), rendered to 96px PNGs and shown at 24px: Gmail and Outlook show no
 * SVG. Absolute, like the wordmark.
 */
export const SOCIAL_LINKS = [
  { name: 'Instagram', href: 'https://www.instagram.com/skida.app/', icon: 'https://internal.skida.app/email/social/instagram.png' },
  { name: 'Facebook', href: 'https://www.facebook.com/skidaapp/', icon: 'https://internal.skida.app/email/social/facebook.png' },
  { name: 'YouTube', href: 'https://www.youtube.com/@skida_app', icon: 'https://internal.skida.app/email/social/youtube.png' },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/skida-alpine-adventures', icon: 'https://internal.skida.app/email/social/linkedin.png' },
] as const

/**
 * One section of a message.
 *
 * Every field has a value; nothing is optional and nothing is undefined. A
 * section carrying `background: ''` would render a style attribute with no
 * colour in it, and half the email clients in the world treat that as black.
 */
export interface EmailBlock {
  id: string
  kind: BlockKind
  /**
   * Who sees this section. Empty means everybody.
   *
   * Listed rather than expressed as a rule: `['trial', 'free']` is a sentence a
   * person can check against the preview, and `tier != 'paid'` is not.
   */
  visibleTo: ReaderSegment[]
  /**
   * The languages this section is for, as Skida language codes — 28 Oct 2026.
   * Empty or absent means every language. Combined with `visibleTo` and
   * `countries` by AND: each that is set has to hold.
   */
  languages?: string[]
  /** The countries this section is for, as ISO codes. Empty or absent is all. */
  countries?: string[]
  /** CSS colour for the section's own band. */
  background: string
  /** Text colour inside it, so a dark band is readable without editing every word. */
  color: string
  /** Space above the content — and below it too, unless `paddingBottom` says otherwise. */
  padding: BlockPadding
  /**
   * Space below the content, when it differs from the top — 2 Nov 2026.
   * Absent on every section made before, which keeps them as they were.
   */
  paddingBottom?: BlockPadding
  align: BlockAlign
  /** `text` — the rich text editor's own markup. */
  html: string
  /** `image` — an absolute URL. A relative one resolves against nothing in an inbox. */
  src: string
  /**
   * The picture as it was uploaded, before any crop.
   *
   * Kept so a ratio can be changed twice without re-uploading. `src` is the
   * cropped copy that actually goes in the message; this one is never rendered.
   * Cropping in the browser and storing the result is the only way that works —
   * `object-fit` is a no-op in Outlook, and a table cell cannot clip an image.
   */
  srcOriginal: string
  /**
   * The crop, as `w:h`. Empty means the picture's own shape.
   *
   * Applied at upload and re-applied from `srcOriginal` when it changes, so the
   * stored file is already the right shape and no client has to be trusted to
   * crop it on the way in.
   */
  ratio: string
  alt: string
  /** `button` — the label and where it goes. */
  label: string
  href: string
  /**
   * Which of a component's two forms this is.
   *
   * `header` and `footer` each have two in the handover — light or dark, and
   * marketing or transactional — and a `card` has one with a thumbnail and one
   * without. On an `image` it is the width: `full` bleeds to the edge of the
   * column, `natural` leaves the picture at its own size, and empty is the
   * content column inside its padding. Empty means the first of whatever the
   * kind offers.
   */
  variant: string
  /**
   * The rows of a component that has them.
   *
   * A stat cell is a figure and what it counts; a step is a title and the line
   * under it; a card's lines are its title, place and detail. One shape for all
   * three, because three nearly-identical arrays is three places to fix the
   * same bug.
   */
  items: { a: string; b: string }[]
  /**
   * `image` — text laid over the picture, 13 Oct 2026. `text: ''` means none.
   *
   * Absent on every block written before it, so readers go through
   * `overlayOf`, which fills the defaults — never read this field directly.
   */
  overlay?: ImageOverlay
}

/** Where a box sits inside its picture — like cell alignment in Word. */
export type OverlayVAlign = 'top' | 'middle' | 'bottom'

export interface ImageOverlay {
  /** Plain text; line breaks are kept. Empty means no overlay at all. */
  text: string
  /** Text colour, hex. */
  color: string
  /** Text size in px. Clamped to 10–72 when rendered. */
  size: number
  /** The box behind the text, hex. */
  background: string
  /** How solid that box is, 0–100. 0 is fully see-through: text straight on the picture. */
  opacity: number
  hAlign: BlockAlign
  vAlign: OverlayVAlign
  /**
   * The picture's height over its width, measured when it was chosen.
   *
   * An email cannot lay text over an `<img>` — Outlook has no positioning — so
   * an overlaid picture is drawn as a cell's background, and a background has
   * no height of its own. This gives the cell one. A crop's ratio wins over it.
   * 0 when not yet measured.
   */
  aspect: number
}

export const NO_OVERLAY: ImageOverlay = {
  text: '',
  color: '#FFFFFF',
  size: 28,
  background: '#000000',
  opacity: 40,
  hAlign: 'center',
  vAlign: 'middle',
  aspect: 0,
}

/** A block's overlay with every field present, whatever was stored. */
export function overlayOf(b: Pick<EmailBlock, 'overlay'>): ImageOverlay {
  return { ...NO_OVERLAY, ...(b.overlay ?? {}) }
}

export interface EmailDocument {
  /** The page behind the message, outside the content column. */
  background: string
  /** The content column, in pixels. 600 is what every email client expects. */
  width: number
  blocks: EmailBlock[]
  /**
   * The line an inbox shows after the subject — 14 Oct 2026. Rendered as hidden
   * text at the very top of the message, which is where every client takes it
   * from. Empty means the inbox shows the first words of the email instead.
   */
  preheader?: string
}

export const EMPTY_DOCUMENT: EmailDocument = {
  background: EMAIL_THEME.page,
  // 600 is what the handover specifies and what every email client expects.
  width: 600,
  blocks: [],
}

/**
 * A new section, with the handover's own defaults already in it.
 *
 * The whole reason these are separate kinds: a writer adding a stat row gets
 * 26px/700 on #F4F6F8 because that is what the design says, not because they
 * remembered. Only the words are theirs to fill in.
 */
export function newBlock(kind: BlockKind): EmailBlock {
  const base: EmailBlock = {
    // Unique enough for a list key and for a round trip through Firestore.
    id: `b${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`,
    kind,
    visibleTo: [],
    languages: [],
    countries: [],
    background: EMAIL_THEME.surface,
    color: EMAIL_THEME.body,
    padding: 'medium',
    align: 'left',
    html: '',
    src: '',
    srcOriginal: '',
    ratio: '',
    alt: '',
    label: '',
    href: '',
    variant: '',
    items: [],
  }

  switch (kind) {
    case 'text':
      return { ...base, html: '<p>Write here.</p>' }

    case 'heading':
      // The handover's h1 for an email: 26px/600 on #111212, tight.
      return { ...base, label: 'A short, plain headline', color: EMAIL_THEME.heading }

    case 'header':
      return { ...base, variant: 'light', padding: 'medium' }

    case 'button':
      // Pill, indigo-blue, white — one CTA per email, per the spec. The default
      // destination is the map, because a button labelled "Open Skida" that goes
      // nowhere is the commonest way a newsletter ships broken: the label reads
      // as finished, so nobody notices the empty field under it.
      return {
        ...base,
        label: 'Open Skida',
        href: 'https://app.skida.app/map/',
        color: EMAIL_THEME.primary,
        align: 'left',
      }

    case 'notice':
      // "The most-read line" in five of the twenty-three emails.
      return { ...base, label: 'ACCESS RUNS TO', html: '14 October 2026' }

    case 'stats':
      return {
        ...base,
        items: [
          { a: '17', b: 'routes' },
          { a: '3', b: 'collections' },
          { a: '12', b: 'days out' },
        ],
      }

    case 'card':
      return {
        ...base,
        variant: 'plain',
        items: [
          { a: 'Store Blåmann, NW couloir', b: '' },
          { a: 'Kvaløya, Troms', b: '' },
          { a: 'Max 38° · NW aspect · 890 m ascent', b: '' },
        ],
      }

    case 'steps':
      return {
        ...base,
        items: [
          { a: 'Your region', b: '' },
          { a: 'Your skill level', b: '' },
        ],
      }

    case 'footer':
      return {
        ...base,
        variant: 'marketing',
        padding: 'small',
        color: EMAIL_THEME.muted,
        html: 'Mountainsnow AS · Strandgata 6, 4790 Lillesand, Norway',
      }

    case 'image':
      return { ...base, overlay: { ...NO_OVERLAY } }

    case 'divider':
    case 'spacer':
      return { ...base, padding: 'small' }
  }
}

/**
 * The sections every message starts with.
 *
 * A header, a headline, something to say and a footer — the minimum the
 * handover treats as an email rather than a fragment. A blank canvas produced
 * messages with no wordmark and no unsubscribe, which is a legal problem as
 * much as a design one, and "remember to add the footer" is not a system.
 */
export function starterBlocks(): EmailBlock[] {
  return [newBlock('header'), newBlock('heading'), newBlock('text'), newBlock('footer')]
}

/** Whether any section is addressed at a subset of readers. */
export const hasConditions = (doc: EmailDocument): boolean =>
  doc.blocks.some((b) => b.visibleTo.length > 0 || (b.languages?.length ?? 0) > 0 || (b.countries?.length ?? 0) > 0)

/** The tiers a document actually distinguishes, for the preview's tabs. */
export function tiersUsed(doc: EmailDocument): ReaderSegment[] {
  const used = new Set<ReaderSegment>()
  for (const b of doc.blocks) for (const t of b.visibleTo) used.add(t)
  return READER_TIERS.map((t) => t.key).filter((t) => used.has(t))
}
